import { useEffect, useMemo, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";
import { PenNibIcon } from "@phosphor-icons/react";

import { storage } from "@/shared/utils/local-storage";
import { NotesInitializationPlaceholder } from "@/features/notes/components/Notes/NotesInitializationPlaceholder";
import { NotesEmptyPlaceholders } from "@/features/notes/components/Notes/NotesEmptyPlaceholders";
import { NotesSelectionToolbar } from "@/features/notes/components/Notes/NotesSelectionToolbar";
import { NotesSearchAndFilters, NotesFilterBadge } from "@/features/notes/components/Notes/NotesSearchAndFilters";
import { FetchedTag } from "@/services/api/fetchers/tags";
import { useNotesState } from "@/features/notes/hooks/useNotesState";
import { useNotesHandlers } from "@/features/notes/hooks/useNotesHandlers";
import { useNotesActions } from "@/features/notes/hooks/useNotesActions";
import { useFoldersHandlers } from "@/features/folders/hooks/useFoldersHandlers";
import { useBoundStore } from "@/app/store";
import { useMenuItems } from "@/shared/hooks/useMenuItems";
import { InstallPWAToast } from "@/shared/components/common/InstallPWAToast";
import { ManageFolder } from "@/features/folders/components/ManageFolder";
import { FoldersAndNotesList } from "@/features/notes/components/Notes/FoldersAndNotesList";
import { useOpenState } from "@/shared/hooks/useOpenState";
import { useFolderActions } from "@/features/folders/hooks/useFolderActions";
import { FetchedFolder } from "@/services/api/fetchers/folders";
import { ConfirmDeleteFolder } from "@/features/folders/components/ConfirmDeleteFolder";
import { FolderFormValues } from "@/features/folders/components/FolderFormContent";
import { usePageLayout } from "@/app/hooks/usePageLayout";
import { PAGE_LAYOUT_ID } from "@/features/notes/config";

export function NotesRoute() {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const currentFolderId = folderId === "0" ? null : Number(folderId);

  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const userId = useBoundStore(useShallow((state) => state.user.id));
  const viewMode = useBoundStore(useShallow((state) => state.settings.notes.viewMode));
  const addFolder = useBoundStore(useShallow((state) => state.folders.addFolder));
  const folders = useBoundStore(useShallow((state) => state.folders.main));
  const deleteFolder = useBoundStore(useShallow((state) => state.folders.deleteFolder));
  const updateFolder = useBoundStore(useShallow((state) => state.folders.updateFolder));

  const notesState = useNotesState(currentFolderId);
  const {
    // State
    isSelectionMode,
    searchParams,
    selectedNotes,
    selectedFolders,
    selectedTagIds,
    selectedTags,
    setIsSelectionMode,
    setSelectedNotes,
    setSelectedFolders,
    setSelectedTagIds,
    setSelectedTags,
    // Store data
    addNote,
    hasNotes,
    notes,
    tags,
    tempDeleteNote,
    updateNote,
    // Handlers
    clearSelection,
    handleTagsFilterChange,
    setSearchParams,
  } = notesState;
  const notesHandlers = useNotesHandlers({
    isSelectionMode,
    notes,
    searchParams,
    selectedNotes,
    selectedFolders,
    setIsSelectionMode,
    setSearchParams,
    setSelectedNotes,
    tempDeleteNote,
    updateNote,
    clearSelection,
    folderId,
  });
  const foldersHandlers = useFoldersHandlers({
    selectedFolders,
    setSelectedFolders,
    selectedNotes,
    isSelectionMode,
    setIsSelectionMode,
    deleteFolder,
    clearSelection,
  });

  // Folder modal state
  const {
    id: manageFolderId,
    isOpen: isFolderModalOpen,
    handleOpenChange: handleManageFolderOpenChange,
  } = useOpenState({ id: "manage-folder" });
  const {
    id: confirmDeleteFolderId,
    isOpen: isConfirmDeleteFolderOpen,
    handleOpenChange: handleConfirmDeleteFolderOpenChange,
  } = useOpenState({ id: "confirm-delete-folder" });
  const [editingFolder, setEditingFolder] = useState<FetchedFolder>();
  const [folderToDelete, setFolderToDelete] = useState<{ data: FetchedFolder; backCount: number }>();
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Get current folder name for the title
  const currentFolder = useMemo(() => {
    if (currentFolderId === null) return null;
    return folders.find((folder) => folder.id === currentFolderId) ?? null;
  }, [folders, currentFolderId]);

  // Folder handlers
  const handleDeleteFolder = useCallback(
    (folder: FetchedFolder | undefined, backCount: number) => {
      if (!folder) {
        return;
      }

      setFolderToDelete({ data: folder, backCount });
      handleConfirmDeleteFolderOpenChange(true);
    },
    [handleConfirmDeleteFolderOpenChange]
  );

  const handleEditFolder = useCallback(
    (folder: FetchedFolder | null) => {
      if (!folder) {
        return;
      }

      setEditingFolder(folder);
      handleManageFolderOpenChange(true);
    },
    [handleManageFolderOpenChange]
  );

  const handleSaveFolder = useCallback(
    async (values: FolderFormValues, folderId: number | null) => {
      if (!userId) return;

      if (folderId) {
        // Editing existing folder
        updateFolder(folderId, {
          name: values.name.trim(),
          color: values.color,
        });
      } else {
        // Creating new folder
        addFolder({
          name: values.name.trim(),
          color: values.color ?? null,
          parent_id: currentFolderId ?? null, // Use current folder as parent when creating subfolder
          user_id: userId,
        });
      }
      setEditingFolder(undefined);
    },
    [addFolder, updateFolder, userId, currentFolderId]
  );

  // Folder actions
  const folderActions = useFolderActions({
    editFolder: handleEditFolder,
    deleteFolder: (folder) => handleDeleteFolder(folder, -1),
  });

  // Check if there's an active search or tag filter
  const hasActiveSearch = useMemo(() => {
    return Boolean(searchParams.get("q") || selectedTags.length > 0);
  }, [searchParams, selectedTags]);

  // Filter notes by current folder (already handled in useNotesState)
  const filteredNotes = notesState.filteredNotes || [];

  // Filter folders based on current folder context
  const filteredFolders = useMemo(() => {
    return folders.filter((folder) => {
      // For root folder (folderId = 0), show folders without a parent or with parent_id = null
      if (!currentFolderId) {
        return !folder.parent_id || folder.parent_id === null;
      }
      // For specific folders, show subfolders that belong to that folder
      return folder.parent_id === currentFolderId;
    });
  }, [folders, currentFolderId]);

  const shouldShowFolders = !hasActiveSearch;

  // Show back button when not in root folder
  const shouldShowBackButton = currentFolderId !== null && !isSelectionMode;

  // Determine the page title
  const pageTitle = useMemo(() => {
    if (isSelectionMode) {
      const totalSelected = selectedNotes.size + selectedFolders.size;
      return `${totalSelected} selected`;
    }
    if (currentFolder) {
      return currentFolder.name;
    }
    return "Notes";
  }, [isSelectionMode, selectedNotes.size, selectedFolders.size, currentFolder]);

  // Handler to show bulk delete confirmation (only when folders are selected)
  const handleShowBulkDeleteConfirmation = useCallback(() => {
    // If only notes are selected (no folders), delete immediately without confirmation
    if (selectedFolders.size === 0) {
      notesHandlers.handleDeleteSelected();
      return;
    }

    // If folders are selected (with or without notes), show confirmation
    setIsBulkDelete(true);
    handleConfirmDeleteFolderOpenChange(true);
  }, [selectedFolders.size, notesHandlers, handleConfirmDeleteFolderOpenChange]);

  // Combine delete handlers from both notes and folders
  const handleDeleteSelectedCombined = useCallback(() => {
    notesHandlers.handleDeleteSelected(); // Delete notes
    foldersHandlers.handleDeleteSelected(); // Delete folders
    setIsBulkDelete(false);
  }, [notesHandlers, foldersHandlers]);

  // Handler for confirming folder deletion (both single and bulk)
  const handleConfirmDeleteFolder = useCallback(() => {
    if (isBulkDelete) {
      // Handle bulk delete
      handleDeleteSelectedCombined();
      navigate(-1);
    } else if (folderToDelete) {
      // Handle single folder delete
      navigate(folderToDelete.backCount);
      deleteFolder(folderToDelete.data.id);
    }
  }, [isBulkDelete, folderToDelete, deleteFolder, navigate, handleDeleteSelectedCombined]);

  const actions = useNotesActions({ userId, addNote, tempDeleteNote, updateNote });
  const menuItems = useMenuItems("notes", {
    initialValues: currentFolder,
    onDeleteFolder: () => (currentFolder ? deleteFolder(currentFolder?.id) : undefined),
    onEditFolder: (values) => handleSaveFolder(values, currentFolderId),
    onNewFolder: (values) => handleSaveFolder(values, null),
    folder: currentFolder,
  });

  // Handle scroll position restoration
  useEffect(() => {
    const state = storage.getNotesNavigationState();

    if (state.scrollPosition) {
      document.getElementById(PAGE_LAYOUT_ID)?.scrollTo(0, Number(state.scrollPosition));
      storage.removeNotesScrollPosition();
    }
  }, [notesHandlers.layoutScrollTop]);

  // Memoized tags change handler
  const handleTagsChangeOptimized = useCallback(
    (newTags: FetchedTag[]) => {
      setSelectedTags(newTags);
      setSelectedTagIds(newTags.map((tag) => tag.id.toString()));
      notesHandlers.handleTagsChange(newTags);
    },
    [setSelectedTags, setSelectedTagIds, notesHandlers]
  );

  const showPlaceholders = useMemo(() => {
    // Rule 1: Show if there are no notes and no folders
    if (filteredNotes.length === 0 && filteredFolders.length === 0) {
      return true;
    }
    // Rule 2: Don't show if notes or folders exist (handled above - return false)
    // Rule 3: Show if filter/search is active and no notes found (but folders can exist)
    if (hasActiveSearch && filteredNotes.length === 0) {
      return true;
    }
    return false;
  }, [filteredNotes.length, filteredFolders.length, hasActiveSearch]);

  // Memoized bottom navigation
  const fab = useMemo(
    () => ({ icon: <PenNibIcon />, onPress: notesHandlers.handleNewNote, isAnimated: true }),
    [notesHandlers]
  );

  // Memoized scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      notesHandlers.layoutScrollTop.current = e.currentTarget.scrollTop;
    },
    [notesHandlers.layoutScrollTop]
  );

  // Memoized navbar actions
  const navbarActions = useMemo(() => {
    if (isSelectionMode) {
      const toolbarActions = NotesSelectionToolbar({
        currentFolderId,
        selectedNotesCount: selectedNotes.size,
        selectedNoteIds: selectedNotes,
        selectedFoldersCount: selectedFolders.size,
        selectedFolderIds: selectedFolders,
        onPinSelected: notesHandlers.handlePinSelected,
        onLockSelected: notesHandlers.handleLockSelected,
        onMaskSelected: notesHandlers.handleMaskSelected,
        onDeleteSelected: handleShowBulkDeleteConfirmation,
        onClearSelection: clearSelection,
      });
      return toolbarActions || [];
    } else if (deviceType === "mobile") {
      return [
        <NotesFilterBadge
          key="filter-tags-badge"
          selectedTags={selectedTags}
          selectedTagIds={selectedTagIds}
          onTagsFilterChange={handleTagsFilterChange}
        />,
      ];
    }
    return [];
  }, [
    isSelectionMode,
    selectedNotes,
    selectedFolders,
    currentFolderId,
    notesHandlers.handlePinSelected,
    notesHandlers.handleLockSelected,
    notesHandlers.handleMaskSelected,
    handleShowBulkDeleteConfirmation,
    clearSelection,
    deviceType,
    selectedTags,
    selectedTagIds,
    handleTagsFilterChange,
  ]);

  // Handle ESC key to navigate back when inside a folder
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isSelectionMode) {
          clearSelection();
          return;
        }

        if (currentFolderId !== null) {
          navigate(-1);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentFolderId, isSelectionMode, folders, clearSelection, navigate]);

  // Layout config with atomic slot updates via Zustand
  // Handlers are memoized and dependencies properly tracked
  // The slot architecture prevents layout re-renders when individual slots update
  const pageLayoutConfig = useMemo(
    () => ({
      showBottomNavigation: true,
      fab,
      navbar: {
        actions: navbarActions,
        hideBack: !shouldShowBackButton,
        input: (
          <NotesSearchAndFilters
            deviceType={deviceType}
            hasNotes={hasNotes}
            isSelectionMode={isSelectionMode}
            tags={tags}
            selectedTags={selectedTags}
            onSearchChange={notesHandlers.handleSearch}
            onTagsChange={handleTagsChangeOptimized}
          />
        ),
        menuItems: isSelectionMode ? [] : menuItems,
        title: pageTitle,
      },
      onScroll: handleScroll,
    }),
    [
      fab,
      navbarActions,
      shouldShowBackButton,
      deviceType,
      hasNotes,
      isSelectionMode,
      tags,
      selectedTags,
      notesHandlers.handleSearch,
      handleTagsChangeOptimized,
      menuItems,
      pageTitle,
      handleScroll,
    ]
  );

  // Configure page layout with atomic selectors - must be called before any early returns
  usePageLayout(pageLayoutConfig);

  if (!notes) {
    return <NotesInitializationPlaceholder notes={notes} />;
  }

  return (
    <>
      <InstallPWAToast />

      {showPlaceholders ? (
        <NotesEmptyPlaceholders
          hasNotes={hasNotes && filteredNotes.length > 0}
          filteredNotesLength={filteredNotes.length}
          hasSearchOrFilters={hasActiveSearch}
          deviceType={deviceType}
        />
      ) : (
        <FoldersAndNotesList
          folders={shouldShowFolders ? filteredFolders : []}
          notes={filteredNotes}
          folderActions={folderActions}
          noteActions={actions}
          onFolderPress={foldersHandlers.handleFolderPress}
          onNotePress={notesHandlers.handlePress}
          selectedNotes={selectedNotes}
          selectedFolders={selectedFolders}
          isSelectionMode={isSelectionMode}
          viewMode={deviceType === "mobile" ? viewMode : "grid"}
        />
      )}

      <ManageFolder
        id={manageFolderId}
        initialValues={editingFolder}
        isOpen={isFolderModalOpen}
        onOpenChange={handleManageFolderOpenChange}
        onSave={(values) => handleSaveFolder(values, editingFolder?.id ?? null)}
      />
      <ConfirmDeleteFolder
        id={confirmDeleteFolderId}
        folder={folderToDelete?.data}
        folderCount={isBulkDelete ? selectedFolders.size : 0}
        noteCount={isBulkDelete ? selectedNotes.size : 0}
        isOpen={isConfirmDeleteFolderOpen}
        isBulkDelete={isBulkDelete}
        onOpenChange={handleConfirmDeleteFolderOpenChange}
        onConfirm={handleConfirmDeleteFolder}
      />
    </>
  );
}
